import React, { useEffect, useRef } from 'react';
import Transition from '../../components/Transition/Transition';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from '../../components/Footer/Footer';
import './MobileAbout.css';

const MobileAbout = () => {
  const contentRef = useRef(null);
  const textRefs = useRef([]);
  const titleRefs = useRef([]);
  const imageRefs = useRef([]);

  // Register ScrollTrigger plugin
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
  }, []);

  // Add viewport meta tag for proper mobile rendering
  useEffect(() => {
    // Check if viewport meta exists
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    
    // If not, create one
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.getElementsByTagName('head')[0].appendChild(viewportMeta);
    }
    
    // Set content attribute with width=device-width
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
  }, []);

  // Animation for page content
  useEffect(() => {
    if (contentRef.current) {
      try {
        // Force layout recalculation before animation
        document.body.offsetHeight;
        
        gsap.fromTo(
          contentRef.current,
          { opacity: 0, y: 20 },
          { 
            opacity: 1, 
            y: 0, 
            duration: 0.8, 
            ease: "power2.out",
            onComplete: () => {
              // Add a class to the body to indicate the page is loaded
              document.body.classList.add('about-page-loaded');
              
              // Now animate the main title with a slight delay
              const mainTitle = document.querySelector('.call-us-visible-title');
              const subtitle = document.querySelector('.call-us-visible-subtitle');
              
              if (mainTitle && subtitle) {
                gsap.fromTo(
                  mainTitle, 
                  { opacity: 0, y: 30 }, 
                  { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", delay: 0.2 }
                );
                
                gsap.fromTo(
                  subtitle, 
                  { opacity: 0, y: 20 }, 
                  { opacity: 1, y: 0, duration: 0.8, ease: "power2.out", delay: 0.4 }
                );
              }
            }
          }
        );
      } catch (error) {
        // Still mark as loaded even if animation fails
        document.body.classList.add('about-page-loaded');
      }
    }
  }, []);

  // Create scroll animations for all text elements
  useEffect(() => {
    // Animate text paragraphs on scroll
    textRefs.current.forEach((textRef) => {
      if (textRef) {
        gsap.fromTo(
          textRef,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: textRef,
              start: "top 80%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }
    });

    // Animate section titles with stagger effect
    titleRefs.current.forEach((titleRef) => {
      if (titleRef) {
        const titleElements = titleRef.querySelectorAll('.heading-text');
        
        gsap.fromTo(
          titleElements,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6, 
            stagger: 0.1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef,
              start: "top 75%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }
    });

    // Animate images with subtle scale effect
    imageRefs.current.forEach((imageRef) => {
      if (imageRef) {
        gsap.fromTo(
          imageRef,
          { opacity: 0.7, scale: 1.05 },
          {
            opacity: 1,
            scale: 1,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: imageRef,
              start: "top 85%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }
    });
  }, []);

  // Ensure proper page setup for direct navigation
  useEffect(() => {
    // Set a proper document title
    document.title = "About Us | VZBL";
    
    // Force scrolling to the top
    window.scrollTo(0, 0);
    
    // Add class to body for page-specific styling
    document.body.classList.add('about-page-active');
    
    return () => {
      // Clean up class when component unmounts
      document.body.classList.remove('about-page-active');
      document.body.classList.remove('about-page-loaded');
    };
  }, []);

  // Fallback image
  const defaultImage = "/about/1.webp";
  
  // Helper function to get image path
  const getImage = (index) => {
    return `/about/${index + 1}.webp`;
  };

  // Handle image errors
  const handleImageError = (e) => {
    e.target.src = defaultImage;
  };

  return (
    <div className="mobile-about dark-theme">
      <div className="mobile-about-content" ref={contentRef}>
        {/* Header with logo */}
        <div className="mobile-header">
         
        </div>
        
        {/* About The Hand Section */}
        <section className="section about-hand-section">
          <div className="call-us-visible-container">
            <div className="call-us-visible-bg">
              <img 
                src="/about/1.webp" 
                alt="Background" 
                className="bg-image" 
                onError={handleImageError}
                ref={el => imageRefs.current[0] = el}
              />
            </div>
            <h1 className="call-us-visible-title">
              Call us<br />visible<span className="asterisk">*</span>
            </h1>
            <p className="call-us-visible-subtitle">(VIZ.Ə.BƏL)</p>
          </div>
          
          <div className="content-block left-aligned">
            <p 
              className="body-text animate-text" 
              ref={el => textRefs.current[0] = el}
            >
              VZBL is all about being seen. It's the frequency at which your brand shows up, whether in search results, on social media, through email, or across other marketing channels. It's about making your brand impossible to ignore, grabbing attention, and building an identity that sticks.
            </p>
          </div>
          
          <div 
            className="section-title hand-title" 
            ref={el => titleRefs.current[0] = el}
          >
            <h2 className="heading-text">About</h2>
            <h2 className="heading-text script">the</h2>
            <h2 className="heading-text">hand</h2>
          </div>
          
          <div className="about-hand-container">
            <div className="about-hand-bg">
              <img 
                src="/about/16.webp" 
                alt="Background" 
                className="about-hand-bg-image" 
                onError={handleImageError}
                ref={el => imageRefs.current[1] = el}
              />
            </div>
          </div>
          
          <div className="content-block hand-content">
            <p 
              className="body-text animate-text" 
              ref={el => textRefs.current[1] = el}
            >
              The hand is more than a symbol, it promises strength, creativity, and unity. It stands for openness and the ability to transform ideas into action. It reflects who we are and what we stand for
            </p>
          </div>
        </section>
        
        {/* Since 2017 Section */}
        <section className="section since-section">
          <div className="since-container">
            <h3 
              className="subheading animate-text" 
              ref={el => textRefs.current[2] = el}
            >
              Since 2017
            </h3>
            <p 
              className="body-text centered animate-text" 
              ref={el => textRefs.current[3] = el}
            >
              A creative agency built to defy the ordinary.
              <br /><br />
              From branding to media production, we deliver work that reveals the unseen.
              <br /><br />
              Our approach is simple: think bold, create smart, and own the spotlight with every project.
            </p>
          </div>
        </section>
        
        {/* The Visionary Mind Section */}
        <section className="section team-section">
          <div 
            className="section-title right-aligned"
            ref={el => titleRefs.current[1] = el}
          >
            <h2 className="heading-text script">The</h2>
            <h2 className="heading-text">visionary /////////mind</h2>
          </div>
        </section>
        
        {/* About The Founder Section */}
        <section className="section founder-section">
          <div 
            className="section-title wide centered"
            ref={el => titleRefs.current[2] = el}
          >
            <h2 className="heading-text">About</h2>
            <h2 className="heading-text script">the</h2>
            <h2 className="heading-text">founder</h2>
          </div>
          
          <div className="team-member-container">
            <div className="team-image-box ">
              <img 
                src={getImage(14)} 
                alt="VZBL Founder"  
                onError={handleImageError}
                ref={el => imageRefs.current[2] = el}
              />
            </div>
            
            <div 
              className="section-title member-title"
              ref={el => titleRefs.current[7] = el}
            >
              <h2 className="heading-text">Ahmed El Zalat</h2>
            </div>
            
            <div className="content-block centered-text">
              <p 
                className="body-text centered animate-text" 
                ref={el => textRefs.current[4] = el}
              >
                Ahmed's curiosity is his greatest strength. It pushes him to explore every brand's story and uncover what others often overlook. He looks beyond the surface, finding hidden insights and unspoken truths that help brands connect on a deeper level. It's his ability to see what's unseen that sets him apart and drives his passion for making brands that truly stand out.
              </p>
            </div>
          </div>
        </section>
        
        {/* Meet The Team Section */}
        <section className="section team-section">
          <div 
            className="section-title wide centered"
            ref={el => titleRefs.current[3] = el}
          >
            <h2 className="heading-text">Meet</h2>
            <h2 className="heading-text script">the</h2>
            <h2 className="heading-text">cco</h2>
          </div>
          
          {/* Team Member 1: Ebraheim El Zeyoudi */}
          <div className="team-member-container">
            <div className="team-image-box member-image">
              <img 
                src="/about/19.webp" 
                alt="Ebraheim El Zeyoudi" 
                onError={handleImageError}
                ref={el => imageRefs.current[3] = el}
              />
            </div>
            <div 
              className="section-title member-title"
              ref={el => titleRefs.current[4] = el}
            >
             
              <h2 className="heading-text">Ebraheim El Zeyoudi</h2>
            </div>
            <div className="content-block member-text">
              <p 
                className="body-text animate-text" 
                ref={el => textRefs.current[6] = el}
              >
                Ebraheim El Zeyoudi is the visionary co-founder and chief 
                commercial officer (CCO) of VZBL, the leading creative 
                marketing agency in the uae and egypt. With a deep 
                understanding of brand positioning and commercial growth, 
                ebraheim has played a pivotal role in transforming vzbl 
                into a powerhouse that bridges the creative gap in the 
                region's marketing landscape.
              </p>
            </div>
          </div>
          

          {/* Team Member 2: Shorouk Abdalla */}
          <div 
            className="section-title wide centered"
            ref={el => titleRefs.current[8] = el}
          >
            <h2 className="heading-text">Meet</h2>
            <h2 className="heading-text script">the</h2>
            <h2 className="heading-text">strategic partner</h2>
          </div>
          <div className="team-member-container">
       
            <div className="team-image-box member-image">
              <img 
                src="/about/18.webp" 
                alt="Shorouk Abdalla" 
                onError={handleImageError}
                ref={el => imageRefs.current[4] = el}
              />
            </div>
            <div 
              className="section-title member-title"
              ref={el => titleRefs.current[5] = el}
            >
              
              <h2 className="heading-text">Shorouk Abdalla</h2>
            </div>
            <div className="content-block member-text">
              <p 
                className="body-text animate-text" 
                ref={el => textRefs.current[7] = el}
              >
                With a strong focus on business development and creative 
                strategy, Shourouk helps clients refine their brand positioning 
                and market presence. As a seasoned restaurateur and hospitality 
                consultant with six years of experience in global markets, she 
                understands the challenges businesses face and works to 
                highlight their strengths and uniqueness, ensuring they stand 
                out in a competitive economy. Through VZBL, she crafts tailored 
                solutions that drive growth, visibility, and lasting impact.
              </p>
            </div>
          </div>
          
       
         
        </section>
        
        {/* Use the existing Footer component */}
        <Footer />
      </div>
    </div>
  );
};

// Export with Transition HOC with explicit settings
export default Transition(MobileAbout, {
  unmountDelay: 800,
  mountDelay: 300,
  appear: true,
}); 